// shared imports
import LocalizableError from '~/shared/src/base/LocalizableError';
import messageCatalog, {__} from '~/shared/src/utilities/messageCatalog';


type Placeholders = {
    [ placeholder : string ] : string;
};

export type ToJsonResult = {
    type : 'stack';
    name : string;
    doNotLocalize : boolean;
    message? : string;
    monoSpaceMessage? : boolean;
    doNotLocalizeMessage? : boolean;
    number? : number;
    placeholders? : Placeholders;
    severity? : 'error' | 'warning';
    position? : never;
    errors : any[];
};

type StackedError = Error | LocalizableError | StackError;

/*interface StackError extends Error {
    type : 'stack';
    placeholders : Placeholders;
    errors : StackedError[];
    toJSON : () => ToJsonResult;
}*/


class StackError extends Error {
    readonly type = 'stack';

    readonly name : string;
    readonly errors : StackedError[] = [];
    readonly placeholders : Placeholders;

    doNotLocalize : boolean = false;
    doNotLocalizeMessage : boolean = false;

    readonly errorSeparatorLength = 60;

    protected _language : string | undefined;
    get language() {
        return this._language;
    }

    set language( language : string | undefined ) {
        this._language = language;
    }

    get nameLocalized() {
        let retVal : string;

        if ( !this.doNotLocalize ) {
            retVal = `${__( this.name, this.placeholders )}`;
        }
        else {
            retVal = this.name;
        }

        return retVal;
    }

    get messageLocalized() {
        let retVal : string | undefined;

        if ( this.message ) {
            if ( !this.doNotLocalize && !this.doNotLocalizeMessage ) {
                retVal = `${__( this.message, this.placeholders )}`;
            }
            else {
                retVal = this.message;
            }
        }

        return retVal;
    }

    constructor( name : string, placeholders : Placeholders = {} ) {
        super( name );

        this.name = name;
        this.message = '';
        this.placeholders = placeholders;
    }

    hasErrors() : boolean {
        return !!this.errors.length;
    }

    add( errors : any | any[] ) : StackError {
        if ( !Array.isArray( errors ) ) {
            errors = [ errors ];
        }

        for ( let error of errors ) {
            if ( !( error instanceof Error ) ) {
                error = new Error( error );
            }

            this.errors.push( error );
        }
        // return this, so the following works: throw( new StackError( 'message' ).add( catchedError ) );
        return this;
    }

    toJSON() : ToJsonResult {
        const retVal = {} as ToJsonResult;

        if ( this.hasErrors() ) {
            retVal.name = this.name;

            if ( this.message && this.message !== this.name ) {
                retVal.message = this.message;

                if ( this.monoSpaceMessage ) {
                    retVal.monoSpaceMessage = this.monoSpaceMessage;
                }

                if ( this.doNotLocalizeMessage ) {
                    retVal.doNotLocalizeMessage = this.doNotLocalizeMessage;
                }
            }

            if ( this.number !== undefined ) {
                retVal.number = this.number;
            }

            if ( this.severity ) {
                retVal.severity = this.severity;
            }

            if ( Object.keys( this.placeholders ).length ) {
                retVal.placeholders = this.placeholders;
            }

            if ( this.doNotLocalize ) {
                retVal.doNotLocalize = this.doNotLocalize;
            }

            retVal.type = this.type;

            retVal.errors = [];
            for ( let errorCount = 0; errorCount < this.errors.length; errorCount++ ) {
                retVal.errors.push( this.errors[ errorCount ].toJSON() );
            }
        }

        return retVal;
    }

    static fromJSON( data : ToJsonResult ) : StackError {
        const retVal = new StackError( data.name, data.placeholders );

        if ( data.message ) {
            retVal.message = data.message;

            if ( data.monoSpaceMessage ) {
                retVal.monoSpaceMessage = data.monoSpaceMessage;
            }

            if ( data.doNotLocalizeMessage ) {
                retVal.doNotLocalizeMessage = data.doNotLocalizeMessage;
            }
        }

        if ( data.number !== undefined ) {
            retVal.number = data.number;
        }

        if ( data.severity ) {
            retVal.severity = data.severity;
        }

        if ( data.doNotLocalize ) {
            retVal.doNotLocalize = data.doNotLocalize;
        }

        if ( data.errors?.length ) {
            for ( let errorCount = 0; errorCount < data.errors.length; errorCount++ ) {
                const errorData = data.errors[ errorCount ];

                let error;

                if ( errorData.type === 'localizable' ) {
                    error = LocalizableError.fromJSON( errorData );
                }
                else if ( errorData.type === 'stack' ) {
                    error = StackError.fromJSON( errorData );
                }
                else {
                    error = Error.fromJSON( errorData );
                }

                retVal.errors.push( error );
            }
        }

        return retVal;
    }

    toString( errorSeparatorLength : number = this.errorSeparatorLength, withPosition : boolean = true ) {
        let retVal = '';

        if ( this.hasErrors() ) {
            const currentLanguage = messageCatalog.language,
                  indent          = ''.indent();

            messageCatalog.language = this._language ?? global.user?.language ?? 'EN';

            if ( !this.doNotLocalize ) {
                retVal = __( this.name, this.placeholders );
            }
            else {
                retVal = this.name;
            }
            if ( retVal === this.name && Object.keys( this.placeholders ).length !== 0 ) {
                retVal += '\nplaceholders: ' + JSON.stringify( this.placeholders );
            }

            if ( this.message && this.message !== this.name ) {
                let message;
                if ( !this.doNotLocalize || !this.doNotLocalizeMessage ) {
                    message = __( this.message, this.placeholders );
                }
                else {
                    message = this.message;
                }
                retVal += `\n${message}`;
                if ( message === this.message && Object.keys( this.placeholders ).length !== 0 ) {
                    retVal += '\nplaceholders: ' + JSON.stringify( this.placeholders );
                }
            }

            if ( errorSeparatorLength > 0 ) {
                retVal += '\n' + '-'.repeat( errorSeparatorLength );
            }

            for ( let errorCount = 0; errorCount < this.errors.length; errorCount++ ) {
                const error = this.errors[ errorCount ];

                let errorString : string;

                if ( error instanceof LocalizableError || error instanceof StackError ) {
                    const currentErrorLanguage = error.language;

                    error.language = this._language;

                    if ( error instanceof LocalizableError ) {
                        errorString = error.toString( withPosition );
                    }
                    else {
                        errorString = error.toString( errorSeparatorLength - indent.length, withPosition );
                    }

                    error.language = currentErrorLanguage;
                }
                else {
                    errorString = error.toString();
                }

                if ( errorString ) {
                    retVal += `\n${indent}${errorString.replace( /(<br>|<br\/>|<br \/>)/mig, '\n' ).split( '\n' ).join( `\n${indent}` )}`;
                }

                // last separator only on top level
                if ( errorSeparatorLength > 0 && ( errorCount < this.errors.length - 1 || errorSeparatorLength === this.errorSeparatorLength ) ) {
                    retVal += '\n' + '-'.repeat( errorSeparatorLength );
                }
            }

            messageCatalog.language = currentLanguage;
        }

        return retVal;
    }

    toHTML( top : boolean = true ) {
        let retVal = '';

        if ( this.hasErrors() ) {
            const currentLanguage = messageCatalog.language;

            messageCatalog.language = this._language ?? global.user?.language ?? 'EN';

            retVal += '<div class="yn-error-subtitle">';
            if ( !this.doNotLocalize ) {
                retVal += `${__( this.name, this.placeholders )}`;
            }
            else {
                retVal += this.name;
            }
            retVal += '</div>';

            if ( this.message && this.message !== this.name ) {
                if ( this.monoSpaceMessage ) {
                    retVal += '<div class="yn-error-message-monospace">';
                }
                else {
                    retVal += '<div class="yn-error-message">';
                }

                let message;
                if ( !this.doNotLocalize || !this.doNotLocalizeMessage ) {
                    message = __( this.message, this.placeholders );
                }
                else {
                    message = this.message;
                }

                message = message.replace( /</g, '&lt;' )
                                 .replace( />/g, '&gt;' )
                                 .replace( /\r?\n/g, '<br/>' );

                if ( this.monoSpaceMessage ) {
                    message = message.replace( / /g, '&nbsp;' )
                                     .replace( /\^/g, '<div class="yn-error-position-marker fas fa-arrow-up"></div>' );
                }

                retVal += message;
                retVal += '</div>';
            }

            retVal += '<hr className="yn-error-ruler"/>';

            for ( let errorCount = 0; errorCount < this.errors.length; errorCount++ ) {
                retVal += '<div class="yn-error-suberror">';

                const error = this.errors[ errorCount ];

                if ( error instanceof LocalizableError || error instanceof StackError ) {
                    const currentErrorLanguage = error.language;

                    error.language = this._language;

                    if ( error instanceof LocalizableError ) {
                        retVal += error.toHTML();
                    }
                    else {
                        retVal += error.toHTML( false );
                    }

                    error.language = currentErrorLanguage;
                }

                else {
                    retVal += error.toHTML();
                }

                retVal += '</div>';

                if ( errorCount < this.errors.length - 1 ) {
                    retVal += '<hr className="yn-error-ruler"/>';
                }

            }

            messageCatalog.language = currentLanguage;
        }

        return retVal;
    }
}

export default StackError;
